import StudentGuideRoute from "./components/student-guide/StudentGuideRoute.jsx";
import SaljaMedKlassenPage from "./components/selling-guide/SellingGuideRoute.jsx";
import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import HomePage from "./pages/HomePage";

import { AuthProvider } from "./state/authContext";
import { CartProvider } from "./state/cartContext.jsx";
import { SupportProvider } from "./state/supportContext.jsx";
import RequireRole from "./components/RequireRole";
import AdminGuard from "./components/AdminGuard";
import AdminDashboardGuard from "./components/AdminDashboardGuard";
import { SiteLayout } from "./components/layout/SiteLayout";
import MiniCart from "./components/MiniCart";
import AnalyticsConsent from "./consent/AnalyticsConsent.jsx";
import { CookieConsentProvider } from "./consent/CookieConsentContext.jsx";
import { SeoHead } from "./components/seo/SeoHead";
import { LEGACY_REDIRECTS } from "./seo/siteMetadata";
import { loadTeamPage } from "./lib/teamPageLoader.js";
import TjanaPengarForeningenPage from "./pages/seo/TjanaPengarForeningenPage";
import {loadGuidePage} from "./lib/guidePageLoader.js";
import {loadClassPage} from "./lib/classPageLoader.js";
import SaFungerarSalespartnerPage from "./pages/seo/SaFungerarSalespartnerPage";
import VanligaFragorPage from "./pages/seo/VanligaFragorPage";
import ProvisionFortjanstPage from "./pages/seo/ProvisionFortjanstPage";

function PageFallback() {
  return <div className="p-6">Laddar…</div>;
}

function lazyRoute(importer) {
  const Page = lazy(importer);
  return function LazyRoutePage(props) {
    return (
      <Suspense fallback={<PageFallback />}>
        <Page {...props} />
      </Suspense>
    );
  };
}

const LazyTeamPage = lazyRoute(loadTeamPage);
const LazyClassPage = lazyRoute(loadClassPage);
const LazyGuidePage=lazyRoute(loadGuidePage);



const Shop = lazyRoute(() => import("./pages/Shop"));
const Login = lazyRoute(() => import("./pages/Login"));
const RegisterGroup = lazyRoute(() => import("./pages/RegisterGroup"));
const RegisterSeller = lazyRoute(() => import("./pages/RegisterSeller"));
const ProductDetails = lazyRoute(() => import("./pages/ProductDetails"));
const ProductsPage = lazyRoute(() => import("./pages/ProductsPage"));
const ContactPage = lazyRoute(() => import("./pages/ContactPage"));
const NotFoundPage = lazyRoute(() => import("./pages/NotFoundPage"));
const SellerPublicPage = lazyRoute(() => import("./pages/seller/SellerPublicPage"));
const GroupPublicPage = lazyRoute(() => import("./pages/group/GroupPublicPage"));
const ResetPassword = lazyRoute(() => import("./pages/auth/ResetPassword"));
const KopvillkorPage = lazyRoute(() => import("./pages/KopvillkorPage"));
const IntegritetspolicyPage = lazyRoute(() => import("./pages/IntegritetspolicyPage"));
const ReturPage = lazyRoute(() => import("./pages/ReturPage"));
const LeveransPage = lazyRoute(() => import("./pages/LeveransPage"));
const GruppledaravtalPage = lazyRoute(() => import("./pages/GruppledaravtalPage"));
const UtbetalningsvillkorPage = lazyRoute(() => import("./pages/UtbetalningsvillkorPage"));
const CookiePolicyPage = lazyRoute(() => import("./pages/CookiePolicyPage"));

const Checkout = lazyRoute(() => import("./pages/Checkout"));
const Success = lazyRoute(() => import("./pages/Success"));
const StyleGuide = lazyRoute(() => import("./pages/StyleGuide"));
const AdminDashboard = lazyRoute(() => import("./pages/dashboards/AdminDashboard"));
const LeaderDashboard = lazyRoute(() => import("./pages/dashboards/LeaderDashboard"));
const SellerDashboard = lazyRoute(() => import("./pages/dashboards/SellerDashboard"));
const AssociationDashboard = lazyRoute(() => import("./pages/dashboards/AssociationDashboard"));
const AdminProductsPage = lazyRoute(() => import("./pages/admin/ProductCatalog"));
const VariantImageReviewPage = lazyRoute(() => import("./pages/admin/VariantImageReviewPage"));
const AdminOrdersPage = lazyRoute(() => import("./pages/admin/AdminOrdersPage"));
const AdminPayoutsPage = lazyRoute(() => import("./pages/admin/AdminPayoutsPage"));
const BatchPicklistPage = lazyRoute(() => import("./pages/admin/BatchPicklistPage"));
const PackBatchPage = lazyRoute(() => import("./pages/admin/PackBatchPage"));
const AdminCreateGroupPage = lazyRoute(() => import("./pages/admin/AdminCreateGroupPage"));
const AdminCreateOrganizationPage = lazyRoute(() => import("./pages/admin/AdminCreateOrganizationPage"));
const AdminOrganizationsPage = lazyRoute(() => import("./pages/admin/AdminOrganizationsPage"));
const AdminEditOrganizationPage = lazyRoute(() => import("./pages/admin/AdminEditOrganizationPage"));
const AdminEditGroupPage = lazyRoute(() => import("./pages/admin/AdminEditGroupPage"));
const AdminFortnoxInvoicesPage = lazyRoute(() => import("./pages/admin/AdminFortnoxInvoicesPage"));
const SupportPanel = lazyRoute(() => import("./pages/admin/SupportPanel"));
const AdminReferralClicksPage = lazyRoute(() => import("./pages/admin/AdminReferralClicksPage"));
const AdminTodayPage = lazyRoute(() => import("./pages/admin/AdminTodayPage"));
const OnboardingWizard = lazyRoute(() => import("./pages/onboarding/OnboardingWizard"));
const SellerProfilePage = lazyRoute(() => import("./pages/seller/SellerProfilePage"));
const GroupMarketingPage = lazyRoute(() => import("./pages/group-admin/GroupMarketingPage"));
const FortnoxCallback = lazyRoute(() => import("./pages/FortnoxCallback"));

export default function App({ initialTeamPage, initialClassPage, initialGuidePage } = {}) {
  const TjanaPengarLagetPage = initialTeamPage || LazyTeamPage;
  const GuidePage=initialGuidePage || LazyGuidePage;
  const KlassforsaljningPage = initialClassPage || LazyClassPage;
  const showStyleGuide = import.meta.env.DEV;

  return (
    <BrowserRouter>
      <CookieConsentProvider>
        <AnalyticsConsent />
        <AuthProvider>
          <CartProvider>
            <SupportProvider>
              <SiteLayout>
                <SeoHead />
                <Routes>
                <Route path="/" element={<HomePage />} />
                {Object.entries(LEGACY_REDIRECTS).map(([from, to]) => (
                  <Route key={from} path={from} element={<Navigate to={to} replace />} />
                ))}
                <Route path="/products" element={<ProductsPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/tjana-pengar-till-laget" element={<TjanaPengarLagetPage />} />
                <Route path="/tjana-pengar-till-foreningen" element={<TjanaPengarForeningenPage />} />
                <Route path="/tjana-pengar-till-klassresan" element={<GuidePage/>}/>
                <Route path="/tjana-pengar-till-studenten" element={<StudentGuideRoute/>}/>
                <Route path="/salja-med-klassen" element={<SaljaMedKlassenPage />} />
                <Route path="/klassforsaljning" element={<KlassforsaljningPage />} />
                <Route path="/sa-fungerar-salespartner" element={<SaFungerarSalespartnerPage />} />
                <Route path="/vanliga-fragor" element={<VanligaFragorPage />} />
                <Route path="/provision-och-fortjanst" element={<ProvisionFortjanstPage />} />
                <Route path="/kopvillkor" element={<KopvillkorPage />} />
                <Route path="/integritetspolicy" element={<IntegritetspolicyPage />} />
                <Route path="/cookiepolicy" element={<CookiePolicyPage />} />
                <Route path="/retur-och-angerratt" element={<ReturPage />} />
                <Route path="/leveransvillkor" element={<LeveransPage />} />
                <Route path="/gruppledaravtal" element={<GruppledaravtalPage />} />
                <Route path="/utbetalningsvillkor" element={<UtbetalningsvillkorPage />} />

                <Route path="/shop" element={<Shop />} />
                <Route path="/shop/products/:productId" element={<ProductDetails />} />
                <Route path="/register-group" element={<RegisterGroup />} />
                <Route path="/register-seller/:token" element={<RegisterSeller />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/success" element={<Success />} />
                <Route path="/login" element={<Login />} />
                <Route path="/auth/reset-password" element={<ResetPassword />} />
                <Route path="/fortnox/callback" element={<FortnoxCallback />} />

                <Route
                  path="/admin"
                  element={
                    <AdminDashboardGuard>
                      <AdminDashboard />
                    </AdminDashboardGuard>
                  }
                />
                <Route
                  path="/admin/orders"
                  element={
                    <RequireRole roles={["admin", "superadmin"]}>
                      <AdminOrdersPage />
                    </RequireRole>
                  }
                />
                <Route
                  path="/admin/payouts/:groupId?"
                  element={
                    <RequireRole roles={["admin", "superadmin"]}>
                      <AdminPayoutsPage />
                    </RequireRole>
                  }
                />
                <Route
                  path="/admin/picklists/direct"
                  element={
                    <RequireRole roles={["admin", "superadmin"]}>
                      <BatchPicklistPage />
                    </RequireRole>
                  }
                />
                <Route
                  path="/admin/pack-batches/:batchId"
                  element={
                    <RequireRole roles={["admin", "superadmin"]}>
                      <PackBatchPage />
                    </RequireRole>
                  }
                />
                <Route
                  path="/admin/products"
                  element={
                    <AdminGuard>
                      <AdminProductsPage />
                    </AdminGuard>
                  }
                />

                <Route
                  path="/admin/variant-image-review"
                  element={
                    <AdminGuard>
                      <VariantImageReviewPage />
                    </AdminGuard>
                  }
                />                <Route
                  path="/admin/groups/new"
                  element={
                    <RequireRole roles={["admin", "superadmin"]}>
                      <AdminCreateGroupPage />
                    </RequireRole>
                  }
                />
                <Route
                  path="/admin/organizations"
                  element={
                    <RequireRole roles={["admin", "superadmin"]}>
                      <AdminOrganizationsPage />
                    </RequireRole>
                  }
                />
                <Route
                  path="/admin/organizations/new"
                  element={
                    <RequireRole roles={["admin", "superadmin"]}>
                      <AdminCreateOrganizationPage />
                    </RequireRole>
                  }
                />
                <Route
                  path="/admin/organizations/:organizationId"
                  element={
                    <RequireRole roles={["admin", "superadmin"]}>
                      <AdminEditOrganizationPage />
                    </RequireRole>
                  }
                />
                <Route
                  path="/admin/groups/:groupId/edit"
                  element={
                    <RequireRole roles={["admin", "superadmin"]}>
                      <AdminEditGroupPage />
                    </RequireRole>
                  }
                />
                <Route
                  path="/admin/support"
                  element={
                    <RequireRole roles={["admin", "superadmin"]}>
                      <SupportPanel />
                    </RequireRole>
                  }
                />
                <Route
                  path="/admin/referral-clicks"
                  element={<RequireRole roles={["superadmin"]}><AdminReferralClicksPage /></RequireRole>}
                />
                <Route
                  path="/admin/today"
                  element={
                    <RequireRole roles={["admin", "superadmin"]}>
                      <AdminTodayPage />
                    </RequireRole>
                  }
                />
                <Route
                  path="/admin/invoices"
                  element={
                    <RequireRole roles={["superadmin"]}>
                      <AdminFortnoxInvoicesPage />
                    </RequireRole>
                  }
                />
                <Route
                  path="/admin/fortnox/invoices"
                  element={
                    <RequireRole roles={["superadmin"]}>
                      <AdminFortnoxInvoicesPage />
                    </RequireRole>
                  }
                />
                <Route
                  path="/group-admin/onboarding/*"
                  element={
                    <RequireRole roles={["group_admin"]}>
                      <OnboardingWizard />
                    </RequireRole>
                  }
                />
                <Route
                  path="/group-admin"
                  element={
                    <RequireRole roles={["group_admin"]}>
                      <LeaderDashboard />
                    </RequireRole>
                  }
                />
                <Route
                  path="/group-admin/marketing"
                  element={
                    <RequireRole roles={["group_admin"]}>
                      <GroupMarketingPage />
                    </RequireRole>
                  }
                />
                {/* Backwards-compat: keep old /leader route but redirect */}
                <Route path="/leader" element={<Navigate to="/group-admin" replace />} />
                <Route
                  path="/seller"
                  element={
                    <RequireRole roles={["seller"]}>
                      <SellerDashboard />
                    </RequireRole>
                  }
                />
                <Route
                  path="/seller/profile"
                  element={
                    <RequireRole roles={["seller"]}>
                      <SellerProfilePage />
                    </RequireRole>
                  }
                />
                <Route path="/seller/public/:id" element={<SellerPublicPage />} />
                <Route path="/group/:id/public" element={<GroupPublicPage />} />
                <Route
                  path="/association"
                  element={
                    <RequireRole roles={["association_manager"]}>
                      <AssociationDashboard />
                    </RequireRole>
                  }
                />

                {showStyleGuide && <Route path="/style-guide" element={<StyleGuide />} />}
                <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </SiteLayout>
              <MiniCart />
            </SupportProvider>
          </CartProvider>
        </AuthProvider>
      </CookieConsentProvider>
    </BrowserRouter>
  );
}
